import React, { useState, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * ScrollToTop Button with Progress Indicator
 * Shows scroll progress as a circular progress bar around a button
 */
export const ScrollToTopButton = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress (0-100)
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollableHeight = documentHeight - windowHeight;
      const progress = (scrollTop / scrollableHeight) * 100;

      setScrollProgress(progress);

      // Show button after scrolling 300px
      setIsVisible(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (!isVisible) return null;

  // Calculate circle parameters for progress
  const size = 64; // Outer size including progress ring
  const innerSize = 56; // Inner button size
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2 - 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (scrollProgress / 100) * circumference;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 z-50 group"
      aria-label="Zurück nach oben"
      style={{ width: size, height: size }}
    >
      {/* SVG Progress Circle */}
      <svg
        className="absolute inset-0 -rotate-90"
        width={size}
        height={size}
        style={{ transform: 'rotate(-90deg)' }}
      >
        {/* Background circle (light gray) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#d1d5db"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle (teal) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#0f766e"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
      </svg>

      {/* Inner button with arrow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-teal-700 hover:bg-teal-800 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 transform group-hover:scale-105"
        style={{ width: innerSize - 8, height: innerSize - 8 }}
      >
        <ArrowUp className="w-6 h-6 text-white" strokeWidth={2.5} />
      </div>
    </button>
  );
};

export default ScrollToTopButton;
